/*
 * enquiry-modal.js — `<enquiry-modal-component>` driver.
 *
 * Listens for the `pdp:open-enquiry` CustomEvent dispatched by
 * the Purchase Form's ATC state machine (assets/product-purchase-
 * form.js) when the resolved state is 'enquiry'. Event detail
 * carries the current variant, which this component uses to:
 *
 *   - Set the hidden Variant ID input to the variant.id
 *   - Show the matching `[data-variant-image]` (default fallback)
 *   - Write variant.title + variant.price into the context card
 *   - Toggle `body.enquiry-modal--show` so CSS reveals the modal
 *
 * Form submission posts to FormSpark via JSON, optionally
 * gated by Botpoison anti-bot challenge. Public key is on
 * the component's `data-botpoison-key`; the loader script tag
 * is in the block markup. We poll for the global `Botpoison`
 * constructor for up to 5s; if it never arrives we fall back
 * to submitting without the challenge.
 *
 * Close handlers — clicking the overlay, the X button, the
 * Continue button (success state), or pressing Escape closes
 * the modal and resets the form back to its initial state.
 */

const COMPONENT = 'enquiry-modal-component';

class EnquiryModalComponent extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.form = this.querySelector('[data-enquiry-form]');
    this.submitButton = this.querySelector('.enquiry-modal__submit');
    this.submitLabel = this.querySelector('.enquiry-modal__submit-label');
    this.submitSpinner = this.querySelector('.enquiry-modal__submit-spinner');
    this.successEl = this.querySelector('.enquiry-modal__success');
    this.errorEl = this.querySelector('.enquiry-modal__error');
    this.variantIdInput = this.querySelector('[data-enquiry-variant-id]');
    this.variantTitleEl = this.querySelector('[data-modal-variant-title]');
    this.variantPriceEl = this.querySelector('[data-modal-price]');
    this.botpoison = null;
  }

  connectedCallback() {
    this.formspark = this.dataset.formsparkId || '';
    this.botpoisonKey = this.dataset.botpoisonKey || '';

    this._bindClose();
    this._bindOpenEvent();
    this._bindEscape();

    if (this.form) {
      this.form.addEventListener(
        'submit',
        (e) => this._handleSubmit(e),
        { signal: this.abortController.signal }
      );
    }

    if (this.botpoisonKey) {
      this._initBotpoison();
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  _bindClose() {
    this.querySelectorAll('[data-enquiry-close]').forEach((el) => {
      el.addEventListener('click', () => this._close(), {
        signal: this.abortController.signal,
      });
    });
  }

  _bindOpenEvent() {
    document.addEventListener(
      'pdp:open-enquiry',
      (event) => this._open(event.detail || {}),
      { signal: this.abortController.signal }
    );
  }

  _bindEscape() {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && document.body.classList.contains('enquiry-modal--show')) {
          this._close();
        }
      },
      { signal: this.abortController.signal }
    );
  }

  _open(detail) {
    const variant = detail.variant || null;
    this._populateVariantContext(variant);
    if (variant && this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }
    document.body.classList.add('enquiry-modal--show');
    // Move focus into the modal — first focusable input. Defers
    // a tick so the body class lands and the modal is on-screen
    // before focus jumps.
    requestAnimationFrame(() => {
      const firstInput = this.form?.querySelector('input:not([type="hidden"]), textarea');
      if (firstInput) firstInput.focus();
    });
  }

  _close() {
    document.body.classList.remove('enquiry-modal--show');
    // Reset form + states after the slide-out finishes so the
    // user doesn't see flicker mid-close.
    setTimeout(() => {
      if (this.form) {
        this.form.hidden = false;
        this.form.reset();
      }
      if (this.successEl) this.successEl.hidden = true;
      this._hideError();
    }, 300);
  }

  _populateVariantContext(variant) {
    // Reset all variant images to hidden, then unhide the matching
    // one (or the `default` fallback if no match).
    this.querySelectorAll('[data-variant-image]').forEach((el) => {
      el.style.display = 'none';
    });
    const variantId = variant?.id != null ? String(variant.id) : '';
    let matched = null;
    if (variantId) {
      matched = this.querySelector(`[data-variant-image="${variantId}"]`);
    }
    const fallback = this.querySelector('[data-variant-image="default"]');
    (matched || fallback)?.style.removeProperty('display');

    // Variant title — only show if non-default ("Default Title"
    // is Shopify's name for single-variant products and is noise).
    if (this.variantTitleEl) {
      const title = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      this.variantTitleEl.textContent = title;
      this.variantTitleEl.hidden = !title;
    }

    if (this.variantPriceEl && variant?.price != null) {
      this.variantPriceEl.innerHTML = this._formatMoney(variant.price);
    }
  }

  /**
   * Lightweight money formatter — variant.price comes in as
   * cents (Shopify's standard product-json shape). Reads the
   * currency symbol from a hidden #currency-symbol input if
   * present; falls back to $. Mirrors v3.0's formatter.
   */
  _formatMoney(cents) {
    const currency =
      document.getElementById('currency-symbol')?.value || '$';
    const value = Math.floor(Number(cents));
    const whole = Math.floor(value / 100);
    const fraction = String(value % 100).padStart(2, '0');
    return `${currency}${whole}.${fraction}`;
  }

  async _initBotpoison() {
    try {
      await this._waitForBotpoison();
      this.botpoison = new window.Botpoison({ publicKey: this.botpoisonKey });
    } catch (err) {
      console.warn('[EnquiryModal] Botpoison init failed:', err);
      // Fall through — form still submits without the challenge.
    }
  }

  _waitForBotpoison() {
    return new Promise((resolve, reject) => {
      if (typeof window.Botpoison !== 'undefined') return resolve();
      let attempts = 0;
      const maxAttempts = 50; // 5s @ 100ms
      const check = () => {
        if (typeof window.Botpoison !== 'undefined') return resolve();
        if (++attempts >= maxAttempts) return reject(new Error('Botpoison load timeout'));
        setTimeout(check, 100);
      };
      check();
    });
  }

  async _handleSubmit(event) {
    event.preventDefault();
    if (!this.form) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this._setLoading(true);
    this._hideError();

    try {
      const formData = new FormData(this.form);
      const payload = Object.fromEntries(formData.entries());

      if (this.botpoison) {
        const { solution } = await this.botpoison.challenge();
        payload._botpoison = solution;
      }

      const response = await fetch(this.form.getAttribute('action'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      this._showSuccess();
    } catch (err) {
      console.error('[EnquiryModal] Submission failed:', err);
      this._showError();
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', String(isLoading));
    if (this.submitLabel) this.submitLabel.hidden = isLoading;
    if (this.submitSpinner) this.submitSpinner.hidden = !isLoading;
  }

  _showSuccess() {
    if (this.form) this.form.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
  }

  _showError() {
    if (this.errorEl) this.errorEl.hidden = false;
  }

  _hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get(COMPONENT)) {
  customElements.define(COMPONENT, EnquiryModalComponent);
}
